<template>
  <s-modal
    v-model="active"
    size="lg"
    hide-header-close
    cancel-confirm-message-code="MC006"
    ok-confirm-message-code="MC011"
    :title="$t('sensors.configuration-dm2.modal.edit-title', {
        contact: this.contact.contactName,
      })"
    :ok-disabled="this.$v.$invalid"
    :ok-title="$t('general.confirm')"
    :is-loading="isLoading"
    :confirm-type="confirmationTypes.YES_NO"
    @show="onShow"
    @cancel="closeModal"
    @ok="submit"
  >
    <b-container class="contact-modal">
      <b-row align-v="center">
        <b-col cols="12">
          <b-form-group>
            <label
              class="required-asterisk"
              v-text="$t('sensors.configuration-dm2.description')"
            />
            <b-row class="align-items-center">
              <b-col
                cols="12"
                lg="9"
              >
                <b-form-input
                  v-model="contact.description"
                  :maxlength="contactFieldsDefinition.description.maxLength"
                  :state="getInputOptionsBootstrap('contact', 'description')"
                  @blur="updateContactDescription"
                />
              </b-col>
              <b-col
                cols="12"
                lg="3"
              >
                <b-form-group class="my-lg-0">
                  <b-form-checkbox
                    class="text-center"
                    v-model="contact.registerStatus"
                    :value="registerStatus.ACTIVE"
                    :unchecked-value="registerStatus.INACTIVE"
                    @change="uncheckAlarm"
                  >
                    {{ this.$t('sensors.configuration-dm2.active-contact')}}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row
        no-gutters
        class="d-flex justify-content-between"
      >
        <b-col
          cols="5"
          lg="2"
        >
          <b-form-group>
            <label
              class="required-asterisk"
              v-text="$t('sensors.configuration-dm2.min-current')"
            />
            <s-number-input
              v-if="showDecimalFields"
              v-model="contact.minCurrent"
              :numeric-options="numericOptions"
              :validation="$v.contact.maxCurrent"
              :invalid="$v.contact.minCurrent.$invalid"
            />
            <div
              v-if="$v.contact.minCurrent.$invalid"
              class="text-danger"
            ></div>
          </b-form-group>
        </b-col>
        <b-col
          cols="5"
          lg="2"
        >
          <b-form-group>
            <label
              class="required-asterisk"
              v-text="$t('sensors.configuration-dm2.max-current')"
            />
            <s-number-input
              v-if="showDecimalFields"
              v-model="contact.maxCurrent"
              :numeric-options="numericOptions"
              :validation="$v.contact.maxCurrent"
              :invalid="$v.contact.maxCurrent.$invalid"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="5"
          lg="2"
        >
          <b-form-group>
            <label
              class="required-asterisk"
              v-text="$t('sensors.configuration-dm2.min-range')"
            />
            <s-number-input
              v-if="showDecimalFields"
              v-model="contact.minRange"
              :numeric-options="numericOptions"
              :validation="$v.contact.maxRange"
              :invalid="$v.contact.minRange.$invalid"
            />
            <div
              v-if="$v.contact.minRange.$invalid"
              class="text-danger"
            ></div>
          </b-form-group>
        </b-col>
        <b-col
          cols="5"
          lg="2"
        >
          <b-form-group>
            <label
              class="required-asterisk"
              v-text="$t('sensors.configuration-dm2.max-range')"
            />
            <s-number-input
              v-if="showDecimalFields"
              v-model="contact.maxRange"
              :numeric-options="numericOptions"
              :validation="$v.contact.maxRange"
              :invalid="$v.contact.maxRange.$invalid"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="5"
          lg="2"
          class="text-left"
        >
          <b-form-group>
            <label
              class="required-asterisk"
              v-text="$t('sensors.configuration-dm2.measuring-unit')"
            />
            <b-form-input
              v-model="contact.measurementUnit"
              :maxlength="contactFieldsDefinition.measurementUnit.maxLength"
              :state="getInputOptionsBootstrap('contact', 'measurementUnit')"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <b-form-checkbox
            class="d-inline-block active-alarm__checkbox"
            v-model="contact.activateAlarm"
            :disabled="!isContactActive"
          >
            {{ $t('sensors.configuration-dm2.modal.activate-alarm') }}
          </b-form-checkbox>
          <info-badge>
            <span v-html="activeAlarmTooltipContent" />
          </info-badge>
        </b-col>
      </b-row>
      <s-fieldset
        v-if="isContactAndAlarmActive"
        class="active-alarm__container"
      >
        <span class="active-alarm__actuation-title">
          {{ $t('sensors.configuration-dm2.modal.actuation-alarm-condition') }}
        </span>
        <b-row>
          <b-col
            cols="6"
            lg="4"
          >
            <b-form-group>
              <label class="required-asterisk">
                {{ $t('sensors.configuration-dm2.modal.reference-field') }}
              </label>
              <b-form-input :value="$t('sensors.configuration-dm2.modal.range')" disabled/>
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            lg="4"
          >
            <b-form-group>
              <label class="required-asterisk">
                {{ $t('sensors.configuration-dm2.modal.condition-field') }}
              </label>
              <s-select
                v-model="contact.condition"
                value-field="id"
                text-field="name"
                :options="conditions"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            lg="4"
          >
            <b-form-group>
              <label class="required-asterisk">
                {{ $t('sensors.configuration-dm2.modal.value') }}
              </label>
              <s-number-input
                v-if="showDecimalFields"
                v-model="$v.contact.value.$model"
                :numeric-options="numericOptions"
                :validation="$v.contact.value"
                :invalid="$v.contact.value.$invalid"
              />
              <div
                v-if="$v.contact.value.$invalid"
                class="text-danger"
              >
                {{ this.getMessageAlert() }}
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </s-fieldset>
      <b-row>
        <alarm-details-component
          :visible-alarm="contact.activateAlarm"
          :details="contact.alarmDetails"
        />
      </b-row>
    </b-container>
  </s-modal>
</template>

<script>
import { InstallationStatus, ActuationAlarmFieldReference } from 'sigma-mappings';
import ContactDm2Service from '@/domain/services/installation/supervision/sensors/contactDm2';
import InfoBadge from '@/components/icon/InfoBadge';
import confirmationTypes from '@/constants/confirmationTypes';
import conditionLookup from '@/domain/data/installation/supervision/sensors/configurationDM2/conditionLookup';
import contactFieldsDefinition from '@/domain/data/installation/supervision/sensors/configurationDM2/contactFieldsDefinition';
import AlarmDetailsComponent from '@/domain/components/installation/supervision/sensors/AlarmDetails';
import MathHelper from '@/services/mathHelper';
import {
  required,
  between,
  maxValue,
  maxLength,
} from 'vuelidate/lib/validators';

export default {
  components: {
    InfoBadge,
    AlarmDetailsComponent,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: String,
    },
    sensorId: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
    },
    usedNames: {
      type: Array,
      default: () => ([]),
    },
  },
  data: vm => ({
    contact: {},
    contactFieldsDefinition,
    loading: true,
    confirmationTypes,
    conditions: conditionLookup(vm),
    selectedContact: false,
  }),

  validations() {
    const value = this.isContactAndAlarmActive
      ? this.getBetweenValidationForValue()
      : {};

    const haveValidName = () => this.haveValidName;
    return {
      contact: {
        measurementUnit: {
          maxLength: maxLength(contactFieldsDefinition.measurementUnit.maxLength),
          required,
        },
        description: {
          required,
          maxLength: maxLength(contactFieldsDefinition.description.maxLength),
          haveValidName,
        },
        minCurrent: {
          between: between(
            contactFieldsDefinition.minCurrent.minValue,
            contactFieldsDefinition.minCurrent.maxValue,
          ),
          maxValue: this.getMaxValueAllowedForMinField(
            this.contact.maxCurrent,
            contactFieldsDefinition.minCurrent.precision,
          ),
          required,
        },
        maxCurrent: {
          between: between(
            contactFieldsDefinition.maxCurrent.minValue,
            contactFieldsDefinition.maxCurrent.maxValue,
          ),
          required,
        },
        minRange: {
          between: between(
            contactFieldsDefinition.minRange.minValue,
            contactFieldsDefinition.minRange.maxValue,
          ),
          maxValue: this.getMaxValueAllowedForMinField(
            this.contact.maxRange,
            contactFieldsDefinition.minRange.precision,
          ),
          required,
        },
        maxRange: {
          between: between(
            contactFieldsDefinition.maxRange.minValue,
            contactFieldsDefinition.maxRange.maxValue,
          ),
          required,
        },
        value,
      },
    };
  },
  methods: {
    getMaxValueAllowedForMinField(value, precision) {
      const maxValueAllowed = value - MathHelper.minValueByPrecision(precision);
      return maxValue(maxValueAllowed);
    },
    updateContactDescription() {
      this.contact.alarmDetails.contactDescription = this.contact.description;
    },
    getMA105() {
      const selectedField = this.contact.referenceField;

      return this.$t('messages.MA105.message', {
        field: this.$t(`sensors.configuration-dm2.${selectedField}`),
      });
    },
    getMA086() {
      const selectedField = this.contact.referenceField;

      const options = field => ({
        minimumFractionDigits: field.precision,
        maximumFractionDigits: field.precision,
      });

      const value = selectedField === ActuationAlarmFieldReference.RANGE
        ? {
          minValue: this.$n(this.contact.minRange, options(contactFieldsDefinition.minRange)),
          maxValue: this.$n(this.contact.maxRange, options(contactFieldsDefinition.maxRange)),
        }
        : {
          minValue: this.$n(this.contact.minCurrent, options(contactFieldsDefinition.minCurrent)),
          maxValue: this.$n(this.contact.maxCurrent, options(contactFieldsDefinition.maxCurrent)),
        };

      return this.$t('messages.MA086.message', value);
    },
    getInputOptionsBootstrap(varName, fieldName) {
      return this.$v[varName][fieldName].$invalid ? false : null;
    },
    getInputOptions(varName, fieldName) {
      const state = this.$v[varName][fieldName].$invalid;

      return {
        state: this.$bsStateValidator(state),
        required: contactFieldsDefinition[fieldName].required,
      };
    },
    getBetweenValidationForValue() {
      const {
        referenceField,
        minCurrent,
        maxCurrent,
        minRange,
        maxRange,
      } = this.contact;

      const rangeValid = () => !this.selectedReferenceRangeInvalid;

      return referenceField === ActuationAlarmFieldReference.RANGE
        ? { between: between(minRange, maxRange), required, rangeValid }
        : { between: between(minCurrent, maxCurrent), required, rangeValid };
    },
    closeModal() {
      this.$emit('close');
    },
    loadContact() {
      this.loading = true;
      return ContactDm2Service.getContactById(this.sensorId, this.contactId)
        .then((data) => {
          const { registerStatus, alarmDetails, ...payload } = data;

          this.contact = {
            ...this.contact,
            ...payload,
            registerStatus: registerStatus ?? InstallationStatus.INACTIVE,
            referenceField: payload?.referenceField ?? ActuationAlarmFieldReference.RANGE,
            condition: payload?.condition ?? this.conditions[0].id,
            alarmDetails: {
              ...alarmDetails,
              contactName: payload?.contactName ?? '',
              contactDescription: payload?.description ?? '',
            },
          };
        })
        .finally(() => { this.loading = false; });
    },
    onShow() {
      if (!this.contactId) {
        this.loading = false;
        this.contact = {};
        return;
      }

      this.loadContact();
    },
    submit() {
      const { alarmDetails, ...payload } = this.contact;
      if (this.isContactAndAlarmActive) {
        this.$emit('submit', payload);

        return;
      }

      const {
        referenceField,
        condition,
        value,
        ...filteredPayload
      } = payload;

      this.$emit('submit', filteredPayload);
    },
    uncheckAlarm() {
      this.contact.activateAlarm = false;
    },
    getMessageAlert() {
      return this.selectedReferenceRangeInvalid ? this.getMA105() : this.getMA086();
    },
  },
  computed: {
    numericOptions() {
      return {
        decimalPlaces: 2,
      };
    },
    haveValidName() {
      return !this.usedNames
        .includes((this.contact?.description || '')
          .trim()
          .toLowerCase());
    },
    selectedReferenceRangeInvalid() {
      return this.contact.referenceField === ActuationAlarmFieldReference.RANGE
        ? (this.$v.contact.minRange.$invalid || this.$v.contact.maxRange.$invalid)
        : (this.$v.contact.minCurrent.$invalid || this.$v.contact.maxCurrent.$invalid);
    },
    isContactActive() {
      return this.contact.registerStatus === InstallationStatus.ACTIVE;
    },
    isAlarmActive() {
      return this.contact?.activateAlarm ?? false;
    },
    isContactAndAlarmActive() {
      return this.isContactActive && this.isAlarmActive;
    },
    activeAlarmTooltipContent() {
      return this.$t('sensors.configuration.contact-hint', {
        alarmsAndWarningsScreenRef: this.alarmsAndWarningsHighlightContent,
      });
    },
    alarmsAndWarningsHighlightContent() {
      return `<a class="dm2-edit-modal__info-highlight-text" href="${this.alarmsAndWarningsUrl
        }"  target="_blank">${this.$t('page-name.alarms-and-warnings')
        }</a>`;
    },
    alarmsAndWarningsUrl() {
      const routeData = this.$router.resolve({
        name: 'alarms-and-warnings',
      });
      return routeData.href;
    },
    showDecimalFields() {
      return this.active && !this.loading;
    },
    registerStatus() {
      return {
        ACTIVE: InstallationStatus.ACTIVE,
        INACTIVE: InstallationStatus.INACTIVE,
      };
    },
  },
};
</script>

<style lang="sass" scoped>
.contact-modal
  transition: height 2s
  label
    white-space: nowrap
  .required-asterisk
    &:after
      content: " *"

.active-alarm
  &__checkbox
    margin-bottom: 1rem
  &__container
    margin-bottom: 1rem
  &__actuation-title
    display: flex
    color: var(--color-primary)
    font-size: 1rem
    font-weight: bold
    padding-bottom: 1rem

::v-deep .dm2-edit-modal
  &__info-highlight-text
    text-decoration: underline
    color: var(--color-info)
    &:hover
      text-decoration: underline
      color: var(--color-info)
      opacity: .8

::v-deep span.select__dropdown-content
  color: var(--font-color-primary)
</style>
